/**
 * Identity Service API Contracts
 * Request/Response schemas for the identity service
 */

import { USER_ROLES } from '@autopia/shared-types';
import { z } from 'zod';

// ============================================================================
// Shared field schemas
// ============================================================================

/** Required + valid email. min(1) surfaces a friendlier "required" message before the "invalid format" one. */
export const RequiredEmailFieldSchema = z.string().min(1).email();

/** Login accepts any existing password -- no complexity enforced here, so legacy passwords created before the complexity policy existed still work. */
export const LoginPasswordFieldSchema = z.string().min(1);

/** Letters, spaces, apostrophes, hyphens only -- shared by RegisterRequestSchema.name and the FE's firstName/lastName inputs. */
export const PERSON_NAME_REGEX = /^[a-zA-Z\s'-]+$/;

/** General phone format: optional leading +, then 8-15 digits. Not restricted to Indonesian numbers -- visitors may register with a foreign phone. */
export const PHONE_REGEX = /^\+?[0-9]{8,15}$/;

/**
 * Derived from the canonical vocabulary rather than re-listing it. This copy
 * happened to match Prisma exactly, which is precisely why it was dangerous:
 * nothing kept it matching. See AGENTS.md rule #7c and #828.
 */
export const UserRoleSchema = z.enum(USER_ROLES);

export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserStatusSchema = z.enum([
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
  'DELETED',
]);

export type UserStatus = z.infer<typeof UserStatusSchema>;

// ============================================================================
// Authentication
// ============================================================================

export const LoginRequestSchema = z.object({
  email: RequiredEmailFieldSchema,
  password: LoginPasswordFieldSchema,
  rememberMe: z.boolean().optional().default(false),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

const LoginUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: UserRoleSchema,
});

/**
 * Login FORM schema -- distinct from `LoginRequestSchema` only in that
 * `rememberMe` stays optional rather than defaulting, so the form's inferred
 * type matches the checkbox's unchecked state.
 *
 * Deliberately carries NO validation messages: an explicit Zod message always
 * beats a locale-aware error map, so baking English copy here would force every
 * app to re-declare the rules just to translate them (and let those copies
 * drift). Apps localise instead via their own error map -- see
 * apps/web/src/lib/zod-i18n.ts and apps/admin/src/lib/zod-i18n.ts. Only
 * field-specific or cross-field copy the map cannot express (regex hints,
 * refinements) belongs in the app.
 */
export const LoginFormSchema = LoginRequestSchema.extend({
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof LoginFormSchema>;

// ADR-013: refreshToken transported via httpOnly cookie; field kept optional for migration.
// tokenType: identity-service's /auth/login response never actually includes this field
// (verified against auth.controller.ts) -- kept optional rather than removed in case a
// future response shape adds it.
export const LoginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().int(),
  tokenType: z.literal('Bearer').optional(),
  user: LoginUserSchema,
  isNewUser: z.boolean().optional()
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * Returned by /auth/login instead of LoginResponseSchema when the account has
 * 2FA/TOTP enabled -- no accessToken yet; caller must complete the TOTP
 * challenge (POST /auth/2fa/verify with tempToken) before receiving one.
 */
export const LoginRequires2FAResponseSchema = z.object({
  requires2FA: z.literal(true),
  tempToken: z.string(),
  user: LoginUserSchema,
});

export type LoginRequires2FAResponse = z.infer<typeof LoginRequires2FAResponseSchema>;

/** The real shape of /auth/login's response -- either a successful login or a 2FA challenge. */
export const LoginResultSchema = z.union([LoginResponseSchema, LoginRequires2FAResponseSchema]);

export type LoginResult = z.infer<typeof LoginResultSchema>;

export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(100),
  name: z.string().min(2).max(100),
  phone: z.string().max(20).optional(),
  acceptTerms: z.boolean().refine((v) => v === true, {
    message: 'You must accept the terms and conditions',
  }),
});

export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/**
 * Bare register form fields, exported without the object-level `.refine()`s so
 * apps can re-apply them with localised copy -- a `.refine()`d schema is a
 * ZodEffects and can't be extended.
 *
 * Field messages are omitted by design -- see the note on `LoginFormSchema`.
 * `acceptTerms` is the exception: a boolean refinement raises a *custom* issue,
 * which an error map cannot translate (it only sees `ctx.defaultError`), so the
 * app must supply that message itself.
 */
export const RegisterFormFieldsSchema = z.object({
  firstName: z.string().min(1).min(2).max(50).regex(PERSON_NAME_REGEX),
  lastName: z.string().max(50).regex(PERSON_NAME_REGEX).optional().or(z.literal('')),
  email: z.string().min(1).email().max(100),
  phone: z.string().max(20).regex(PHONE_REGEX).optional().or(z.literal('')),
  password: z.string().min(1).min(8).max(100),
  confirmPassword: z.string().min(1),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the terms and conditions',
  }),
});

/**
 * Full register FORM schema = fields + object-level refinements (password
 * match, combined-name length). Apps that localise messages re-apply the same
 * two refinements on their extended fields schema -- see register-form.tsx.
 */
export const RegisterFormSchema = RegisterFormFieldsSchema.refine(
  (data) => data.password === data.confirmPassword,
  { message: 'Passwords do not match', path: ['confirmPassword'] }
).refine(
  (data) => {
    const fullName = data.lastName ? `${data.firstName} ${data.lastName}` : data.firstName;
    return fullName.length <= 100;
  },
  { message: 'Full name (first + last) must be at most 100 characters', path: ['lastName'] }
);

export type RegisterFormData = z.infer<typeof RegisterFormSchema>;

export const RegisterResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    role: UserRoleSchema,
  }),
});

export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string().optional(),
});

export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

// ADR-013: refreshToken transported via httpOnly cookie; field kept optional for migration
export const RefreshTokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().int(),
});

export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponseSchema>;

export const LogoutRequestSchema = z.object({
  refreshToken: z.string().optional(),
  allDevices: z.boolean().optional().default(false),
});

export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;

// ============================================================================
// Password Management
// ============================================================================

// Shared ack shape for auth endpoints that only return a status message
// (forgot/reset/change password, verify/resend email, revoke session(s)).
export const MessageResponseSchema = z.object({
  message: z.string(),
});

export type MessageResponse = z.infer<typeof MessageResponseSchema>;

export const ForgotPasswordRequestSchema = z.object({
  email: RequiredEmailFieldSchema,
});

export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

/**
 * Forgot-password FORM schema. The form collects exactly the request body, so
 * this is an alias rather than a separate shape -- kept as a named export so
 * form code reads consistently with the other `*FormSchema`s.
 *
 * Carries no validation messages by design; apps localise via their Zod error
 * map (see the note on `LoginFormSchema`).
 */
export const ForgotPasswordFormSchema = ForgotPasswordRequestSchema;

export type ForgotPasswordFormData = z.infer<typeof ForgotPasswordFormSchema>;

/** Bare fields (no refine) so FE can `.extend()` for local error messages -- see ResetPasswordRequestSchema below for the refined version backend uses. */
export const ResetPasswordRequestFieldsSchema = z.object({
  token: z.string(),
  password: z.string().min(8).max(100),
  confirmPassword: z.string(),
});

export const ResetPasswordRequestSchema = ResetPasswordRequestFieldsSchema.refine(
  (data) => data.password === data.confirmPassword,
  {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  }
);

export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/**
 * Reset-password FORM schema -- distinct from `ResetPasswordRequestSchema`: no
 * `token` field, that comes from the URL.
 *
 * Exported bare (no object-level `.refine()`) because a refined schema is a
 * ZodEffects and cannot be re-refined with localised copy; apps re-apply the
 * password-match refinement with their own message. Field messages are omitted
 * by design -- see the note on `LoginFormSchema`.
 */
export const ResetPasswordFormFieldsSchema = z.object({
  password: z.string().min(1).min(8).max(100),
  confirmPassword: z.string().min(1),
});

export const ResetPasswordFormSchema = ResetPasswordFormFieldsSchema.refine(
  (data) => data.password === data.confirmPassword,
  { message: 'Passwords do not match', path: ['confirmPassword'] }
);

export type ResetPasswordFormData = z.infer<typeof ResetPasswordFormSchema>;

// Regex matches identity-service's ChangePasswordDto/RegisterDto exactly (Matches decorator):
// requires lowercase, uppercase, and a digit; special chars are allowed but not required.
// Exported so FE forms that `.extend()` the bare fields schema can re-apply the
// SAME rule (e.g. apps/web security-content) instead of duplicating it.
export const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]+$/;

/** Bare fields (no refine) so FE can `.extend()` for local error messages -- see ChangePasswordRequestSchema below for the refined version backend uses. */
export const ChangePasswordRequestFieldsSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z
    .string()
    .min(8)
    .max(100)
    .regex(
      STRONG_PASSWORD_REGEX,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string().min(1),
});

export const ChangePasswordRequestSchema = ChangePasswordRequestFieldsSchema.refine(
  (data) => data.newPassword === data.confirmPassword,
  {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  }
);

export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

/**
 * `POST /api/v1/me/security/password` — the signed-in user changing their own.
 *
 * Derived from the fields above rather than redeclared, so the strong-password
 * rule has one definition. `confirmPassword` is dropped: this endpoint has never
 * accepted one and the handler reads only the two fields below. A confirmation
 * field is a FORM concern — it guards against typing a new password wrong twice,
 * which the client checks before submitting.
 */
export const MeChangePasswordSchema = ChangePasswordRequestFieldsSchema.omit({
  confirmPassword: true,
});
export type MeChangePasswordRequest = z.infer<typeof MeChangePasswordSchema>;

/** Change-password FORM schema -- distinct from `ChangePasswordRequestSchema`. */
export const ChangePasswordFormSchema = ChangePasswordRequestFieldsSchema.extend({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'New password must be at least 8 characters')
    .max(100, 'New password must be at most 100 characters')
    .regex(
      STRONG_PASSWORD_REGEX,
      'Must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export type ChangePasswordFormData = z.infer<typeof ChangePasswordFormSchema>;

// ============================================================================
// User Profile
// ============================================================================

export const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  phone: z.string().nullable(),
  role: UserRoleSchema,
  status: UserStatusSchema,
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
  totpEnabled: z.boolean(),
  totpEnabledAt: z.string().datetime().nullable().optional(),
  totpBackupCodesRemaining: z.number().int().nonnegative().optional(),
  referralCode: z.string().nullable().optional(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UpdateProfileRequestSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  phone: z.string().max(20).optional(),
  language: z.enum(['en', 'id']).optional(),
  notificationPreferences: z.object({
    email: z.boolean(),
    sms: z.boolean(),
    whatsapp: z.boolean(),
    push: z.boolean(),
  }).optional(),
});

export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

// ============================================================================
// Email Verification
// ============================================================================

export const VerifyEmailRequestSchema = z.object({
  token: z.string(),
});

export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

export const ResendVerificationRequestSchema = z.object({
  email: z.string().email(),
});

export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequestSchema>;

// ============================================================================
// Admin Customers (Customer Registry — CUSTOMER-role users)
// ============================================================================

/** Status values for CUSTOMER-role users in the admin customer registry */
export const CustomerStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']);

export type CustomerStatus = z.infer<typeof CustomerStatusSchema>;

/** A single row in the admin customer list.
 *  lastEntryAt = gate scan timestamp (null until first park visit).
 *  lastLoginAt = last web/app login timestamp. */
export const CustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  phone: z.string().nullable(),
  status: CustomerStatusSchema,
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  totalOrders: z.number().int().nonnegative(),
  totalSpent: z.number().nonnegative(),
  lastEntryAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
});

export type Customer = z.infer<typeof CustomerSchema>;

/** Paginated response for GET /api/v1/admin/customers */
export const CustomersResponseSchema = z.object({
  customers: z.array(CustomerSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type CustomersResponse = z.infer<typeof CustomersResponseSchema>;

/** Query parameters for GET /api/v1/admin/customers.
 *  dateFrom/dateTo are opt-in filters on lastEntryAt (gate scan date).
 *  Omitting them returns all customers, including those who never entered the park. */
export const CustomersQueryParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  sortBy: z.enum(['createdAt', 'name', 'email', 'lastLoginAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  status: CustomerStatusSchema.optional(),
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export type CustomersQueryParams = z.infer<typeof CustomersQueryParamsSchema>;

// ============================================================================
// Admin Kiosk Visitors
// ============================================================================

/**
 * One row per kiosk **terminal** in the admin Terminal Sales tab.
 *
 * ⚠️ **The name is a leftover and the thing it names is not a visitor.** Since
 * #847/#848 a walk-up order belongs to the terminal's own `User.id`, by design
 * (ADR-037 decision 3) — a till receipt, attributed to the point of sale. #857
 * relabels every user-facing string accordingly; this export keeps its name
 * because renaming a contract type is a separate, mechanical change across four
 * services, and doing it in the same commit as a behaviour change would hide
 * both. **Read `KioskVisitor` as "terminal", and do not add a field on the
 * assumption that a row here is a person.**
 *
 * name = the KIOSK-role user's name in identity-service.
 * stats = aggregated from ticketing-service walk-up orders, summed across both
 *   owner values a kiosk's rows can carry: the terminal's own `User.id` since
 *   #847/#848, and the retired `KIOSK_WALKUP_<kioskId>` sentinel for rows
 *   written before that. #852 backfills the old rows.
 *
 *   ⚠️ The dual read SURVIVES #856 **and #857**. #856 stopped the sentinel
 *   being *written*; the rows that already carry it are still there until
 *   #852's backfill has actually run in the environment being read, and both
 *   `identity-service/src/admin/admin-customers.service.ts` and
 *   `ticketing-service/src/admin/admin-kiosk-visitor-detail.service.ts` still
 *   query on both keys. #857's body says it "drops the second key"; that was
 *   written before the backfill's per-environment ordering was pinned down, and
 *   dropping it early makes a kiosk's sales read low rather than erroring.
 *   Retire it with the rows, not with the producer.
 */
export const KioskVisitorSchema = z.object({
  userId: z.string(),
  kioskId: z.string(),
  name: z.string(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']),
  totalOrders: z.number().int().nonnegative(),
  totalSpent: z.number().nonnegative(),
  lastVisitDate: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type KioskVisitor = z.infer<typeof KioskVisitorSchema>;

export const KioskVisitorsResponseSchema = z.object({
  kiosks: z.array(KioskVisitorSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type KioskVisitorsResponse = z.infer<typeof KioskVisitorsResponseSchema>;

export const KioskVisitorsQueryParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  sortBy: z.enum(['name', 'createdAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']).optional(),
  search: z.string().optional(),
});
export type KioskVisitorsQueryParams = z.infer<typeof KioskVisitorsQueryParamsSchema>;

// ============================================================================
// Staff Management (Admin)
// ============================================================================

export const StaffProfileSchema = z.object({
  id: z.string(),
  userId: z.string(),
  employeeId: z.string(),
  department: z.string().nullable(),
  position: z.string().nullable(),
  hireDate: z.string().nullable(),
  totpEnabled: z.boolean(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
    role: UserRoleSchema,
    status: UserStatusSchema,
  }),
});

export type StaffProfile = z.infer<typeof StaffProfileSchema>;

// `CreateStaffRequestSchema` and `UpdateStaffRequestSchema` lived here and were
// deleted in #958. Both hard-coded `z.enum(['STAFF', 'SUPERVISOR', 'MANAGER',
// 'ADMIN'])` — a fourth, independent role vocabulary that already omitted SALES,
// GATE_OPERATOR, SUPER_ADMIN and KIOSK, and had no call site anywhere in the
// repo. Staff creation and updates use `CreateStaffSchema` / `UpdateStaffSchema`
// in `./staff.ts`, whose role field derives from `ASSIGNABLE_STAFF_ROLES` and so
// cannot name a role that does not exist. Extending a dead list would have made
// the drift look intentional.

export const GetStaffQuerySchema = z.object({
  department: z.string().optional(),
  role: z.string().optional(),
  status: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetStaffQuery = z.infer<typeof GetStaffQuerySchema>;

// ============================================================================
// 2FA / TOTP
// ============================================================================

// Returned by POST /2fa/setup. Backup codes are not issued until /2fa/enable
// confirms the first TOTP code, so this response carries a status message
// instead (see EnableTotpResponseSchema for the backupCodes payload).
export const SetupTotpResponseSchema = z.object({
  secret: z.string(),
  qrCodeDataUrl: z.string(),
  message: z.string(),
});

export type SetupTotpResponse = z.infer<typeof SetupTotpResponseSchema>;

export const EnableTotpResponseSchema = z.object({
  enabled: z.boolean(),
  backupCodes: z.array(z.string()),
  message: z.string(),
});

export type EnableTotpResponse = z.infer<typeof EnableTotpResponseSchema>;

export const DisableTotpRequestSchema = z.object({
  code: z.string().length(6),
  password: z.string(),
});

export type DisableTotpRequest = z.infer<typeof DisableTotpRequestSchema>;

export const DisableTotpResponseSchema = z.object({
  disabled: z.boolean(),
});

export type DisableTotpResponse = z.infer<typeof DisableTotpResponseSchema>;

export const RegenerateBackupCodesResponseSchema = z.object({
  backupCodes: z.array(z.string()),
  message: z.string(),
});

export type RegenerateBackupCodesResponse = z.infer<typeof RegenerateBackupCodesResponseSchema>;

export const TwoFactorStatusResponseSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.string().datetime().nullable().optional(),
  backupCodesRemaining: z.number().int().nonnegative(),
});

export type TwoFactorStatusResponse = z.infer<typeof TwoFactorStatusResponseSchema>;

export const VerifyTotpRequestSchema = z.object({
  code: z.string().length(6),
});

export type VerifyTotpRequest = z.infer<typeof VerifyTotpRequestSchema>;

/** Digits-only 6-character TOTP code, as typed into an authenticator prompt. */
const TOTP_CODE_REGEX = /^\d{6}$/;

/**
 * TOTP code FORM schema -- distinct from `VerifyTotpRequestSchema`, which
 * checks length only: the form also rejects non-digits up front rather than
 * round-tripping to the server.
 *
 * Carries no validation messages by design -- see the note on `LoginFormSchema`.
 */
export const TotpCodeFormSchema = VerifyTotpRequestSchema.extend({
  code: z.string().length(6).regex(TOTP_CODE_REGEX),
});

export type TotpCodeFormData = z.infer<typeof TotpCodeFormSchema>;

/**
 * Disable-2FA FORM schema -- distinct from `DisableTotpRequestSchema`. The
 * backend's DisableTotpDto still has no validation of its own (separate issue),
 * so this is enforced client-side (and by `DisableTotpRequestSchema.parse()` in
 * the mutation hook), not by the server.
 *
 * Carries no validation messages by design -- see the note on `LoginFormSchema`.
 */
export const DisableTotpFormSchema = DisableTotpRequestSchema.extend({
  code: z.string().length(6).regex(TOTP_CODE_REGEX),
  password: z.string().min(1),
});

export type DisableTotpFormData = z.infer<typeof DisableTotpFormSchema>;

export const LoginWithTotpRequestSchema = z.object({
  email: z.string().email(),
  password: z.string(),
  totpCode: z.string().length(6),
});

export type LoginWithTotpRequest = z.infer<typeof LoginWithTotpRequestSchema>;

// ============================================================================
// Sessions
// ============================================================================

// Parsed device information extracted from the request user-agent string
// (mirrors identity-service's Session.deviceInfo JSON column shape).
export const DeviceInfoSchema = z.object({
  browser: z.string(),
  os: z.string(),
  device: z.string(),
});

export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;

export const SessionSchema = z.object({
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  deviceInfo: DeviceInfoSchema.nullable(),
  createdAt: z.string().datetime(),
  lastActiveAt: z.string().datetime(),
  loginMethod: z.string().nullable(),
  isCurrent: z.boolean(),
});

export type Session = z.infer<typeof SessionSchema>;

export const SessionsListResponseSchema = z.array(SessionSchema);
export type SessionsListResponse = z.infer<typeof SessionsListResponseSchema>;

export const RevokeSessionRequestSchema = z.object({
  sessionId: z.string(),
});

export type RevokeSessionRequest = z.infer<typeof RevokeSessionRequestSchema>;

// GET query for DELETE /auth/sessions -- keeps the current session alive
// when `excludeCurrent=true` instead of revoking it too.
export const RevokeAllSessionsQuerySchema = z.object({
  excludeCurrent: z.enum(['true', 'false']).optional(),
});

export type RevokeAllSessionsQuery = z.infer<typeof RevokeAllSessionsQuerySchema>;

// ============================================================================
// OAuth / Social Login (API-012)
// ============================================================================

/**
 * Google OAuth Token Exchange Request
 * Receives the authorization code from the frontend OAuth flow
 */
export const GoogleOAuthRequestSchema = z.object({
  code: z.string().min(1, 'Authorization code is required'),
  redirectUri: z.string().url('Invalid redirect URI').optional(),
  state: z.string().optional(),
});

export type GoogleOAuthRequest = z.infer<typeof GoogleOAuthRequestSchema>;

/**
 * Google OAuth Token Exchange Response
 * Returns JWT tokens after successful OAuth authentication
 */
// ADR-013: refreshToken transported via httpOnly cookie; field kept optional for migration.
// tokenType: identity-service's /auth/oauth/google response never actually includes this
// field (verified against auth.controller.ts) -- kept optional, not required.
export const GoogleOAuthResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().int(),
  tokenType: z.literal('Bearer').optional(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    picture: z.string().url().nullable().optional(),
    emailVerified: z.boolean(),
  }),
  isNewUser: z.boolean(),
});

export type GoogleOAuthResponse = z.infer<typeof GoogleOAuthResponseSchema>;

/**
 * OAuth Provider enum
 */
export const OAuthProviderSchema = z.enum(['google', 'facebook', 'apple']);

export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;

/**
 * OAuth Link Request - Link OAuth provider to existing account
 */
export const OAuthLinkRequestSchema = z.object({
  provider: OAuthProviderSchema,
  code: z.string().min(1, 'Authorization code is required'),
  redirectUri: z.string().url('Invalid redirect URI').optional(),
});

export type OAuthLinkRequest = z.infer<typeof OAuthLinkRequestSchema>;

/**
 * OAuth Unlink Request - Unlink OAuth provider from account
 */
export const OAuthUnlinkRequestSchema = z.object({
  provider: OAuthProviderSchema,
});

export type OAuthUnlinkRequest = z.infer<typeof OAuthUnlinkRequestSchema>;

/**
 * Facebook OAuth Token Exchange Request (S4-01)
 * Receives the authorization code from the frontend Facebook OAuth flow
 */
export const FacebookOAuthRequestSchema = z.object({
  code: z.string().min(1, 'Authorization code is required'),
  redirectUri: z.string().url('Invalid redirect URI').optional(),
  state: z.string().optional(),
});

export type FacebookOAuthRequest = z.infer<typeof FacebookOAuthRequestSchema>;

/**
 * Facebook OAuth Token Exchange Response (S4-01)
 * Returns JWT tokens after successful Facebook OAuth authentication
 */
// ADR-013: refreshToken transported via httpOnly cookie; field kept optional for migration.
// tokenType: identity-service's /auth/oauth/facebook response never actually includes this
// field (mirrors the Google callback) -- kept optional, not required.
export const FacebookOAuthResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number().int(),
  tokenType: z.literal('Bearer').optional(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    picture: z.string().url().nullable().optional(),
    emailVerified: z.boolean(),
  }),
  isNewUser: z.boolean(),
});

export type FacebookOAuthResponse = z.infer<typeof FacebookOAuthResponseSchema>;

/**
 * OAuth Providers Response (S4-02)
 * Returns list of linked OAuth providers for the current user
 */
export const OAuthProviderInfoSchema = z.object({
  provider: z.string(),
  email: z.string().optional(),
  name: z.string().optional(),
  picture: z.string().optional(),
  linkedAt: z.string(),
});

export type OAuthProviderInfo = z.infer<typeof OAuthProviderInfoSchema>;

export const OAuthProvidersResponseSchema = z.array(OAuthProviderInfoSchema);

export type OAuthProvidersResponse = z.infer<typeof OAuthProvidersResponseSchema>;

// ============================================================================
// OTP (One-Time Password) (S4-04)
// ============================================================================

/**
 * OTP Request Schema - Request an OTP code
 */
export const OtpRequestSchema = z.object({
  email: RequiredEmailFieldSchema,
  type: z.enum(['login', 'verify_email']).optional().default('login'),
});

export type OtpRequest = z.infer<typeof OtpRequestSchema>;

/**
 * OTP email-step FORM schema -- distinct from `OtpRequestSchema`: no `type`
 * field, that's supplied at the call site.
 *
 * Carries no validation messages by design; apps localise via their Zod error
 * map (see the note on `LoginFormSchema`).
 */
export const OtpEmailFormSchema = OtpRequestSchema.pick({ email: true });

export type OtpEmailFormData = z.infer<typeof OtpEmailFormSchema>;

/**
 * OTP Verify Schema - Verify an OTP code
 */
export const OtpVerifySchema = z.object({
  email: z.string().email(),
  code: z.string().length(6).regex(/^\d{6}$/, 'OTP must be a 6-digit code'),
  type: z.enum(['login', 'verify_email']).optional().default('login'),
});

export type OtpVerify = z.infer<typeof OtpVerifySchema>;

/**
 * OTP Response Schema - Response after requesting OTP
 */
export const OtpResponseSchema = z.object({
  message: z.string(),
  expiresIn: z.number(),
});

export type OtpResponse = z.infer<typeof OtpResponseSchema>;

/**
 * OTP Verify Response Schema - Response after successful OTP verification with login
 */
export const OtpVerifyResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresIn: z.number(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    emailVerified: z.boolean(),
  }),
});

export type OtpVerifyResponse = z.infer<typeof OtpVerifyResponseSchema>;

// ============================================================================
// Notification Preferences
// ============================================================================

export const NotificationChannelPrefsSchema = z.object({
  marketing: z.boolean(),
  transactional: z.boolean(),
  reminders: z.boolean(),
});
export type NotificationChannelPrefs = z.infer<typeof NotificationChannelPrefsSchema>;

export const NotificationPreferencesSchema = z.object({
  email: NotificationChannelPrefsSchema,
  sms: NotificationChannelPrefsSchema,
  whatsapp: NotificationChannelPrefsSchema,
});
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

export const NotificationPreferencesInputSchema = z.object({
  email: NotificationChannelPrefsSchema.partial().optional(),
  sms: NotificationChannelPrefsSchema.partial().optional(),
  whatsapp: NotificationChannelPrefsSchema.partial().optional(),
});
export type NotificationPreferencesInput = z.infer<typeof NotificationPreferencesInputSchema>;

// ============================================================================
// Change Password Response
// ============================================================================

export const ChangePasswordResponseSchema = z.object({
  message: z.string(),
  sessionsInvalidated: z.number().int(),
});
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

// ============================================================================
// Admin Staff Actions
// ============================================================================

export const AdminActionWithReasonSchema = z.object({
  reason: z.string().optional(),
});
export type AdminActionWithReason = z.infer<typeof AdminActionWithReasonSchema>;

export const ApproveStaffRequestSchema = z.object({
  note: z.string().optional(),
});
export type ApproveStaffRequest = z.infer<typeof ApproveStaffRequestSchema>;

export const RejectStaffRequestSchema = z.object({
  reason: z.string().min(1),
});
export type RejectStaffRequest = z.infer<typeof RejectStaffRequestSchema>;

// ============================================================================
// 2FA Login Verification (post-login challenge)
// ============================================================================

export const Verify2FARequestSchema = z.object({
  tempToken: z.string(),
  code: z.string().length(6),
});
export type Verify2FARequest = z.infer<typeof Verify2FARequestSchema>;

export const VerifyBackupCodeRequestSchema = z.object({
  tempToken: z.string(),
  backupCode: z.string(),
});
export type VerifyBackupCodeRequest = z.infer<typeof VerifyBackupCodeRequestSchema>;

export const VerifyBackupResponseSchema = z.object({
  verified: z.boolean(),
  backupCodesRemaining: z.number().int(),
  warning: z.string().optional(),
});
export type VerifyBackupResponse = z.infer<typeof VerifyBackupResponseSchema>;

export const VerifyTotpResponseSchema = z.object({ verified: z.boolean() });
export type VerifyTotpResponse = z.infer<typeof VerifyTotpResponseSchema>;

// Admin TOTP verify during login (accepts multiple field aliases for backwards compat)
export const AdminVerifyTotpRequestSchema = z.object({
  tempToken: z.string().optional(),
  sessionToken: z.string().optional(),
  code: z.string().optional(),
  totpCode: z.string().optional(),
});
export type AdminVerifyTotpRequest = z.infer<typeof AdminVerifyTotpRequestSchema>;

export const AdminVerifyBackupRequestSchema = z.object({
  tempToken: z.string().optional(),
  sessionToken: z.string().optional(),
  code: z.string().optional(),
  backupCode: z.string().optional(),
});
export type AdminVerifyBackupRequest = z.infer<typeof AdminVerifyBackupRequestSchema>;

// ============================================================================
// Preferences
// ============================================================================

export const SetPreferenceRequestSchema = z.object({
  value: z.unknown(),
});
export type SetPreferenceRequest = z.infer<typeof SetPreferenceRequestSchema>;

export const UserPreferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  value: z.unknown(),
  updatedAt: z.string().datetime(),
});
export type UserPreference = z.infer<typeof UserPreferenceSchema>;

// ============================================================================
// Membership
// ============================================================================

export const MembershipProfileSchema = z.object({
  userId: z.string(),
  membershipTier: z.enum(['NONE', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM']),
  membershipStatus: z.enum(['INACTIVE', 'ACTIVE', 'SUSPENDED', 'EXPIRED']),
  membershipNumber: z.string().nullable(),
  memberSince: z.string().datetime().nullable(),
  membershipExpiresAt: z.string().datetime().nullable(),
  totalLifetimePoints: z.number().int(),
});
export type MembershipProfile = z.infer<typeof MembershipProfileSchema>;

export const CreatePartnerResponseSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: UserRoleSchema,
  referralCode: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type CreatePartnerResponse = z.infer<typeof CreatePartnerResponseSchema>;

// ============================================================================
// OAuth Clients (admin)
// ============================================================================

const OAuthClientTypeSchema = z.enum(['CONFIDENTIAL', 'PUBLIC', 'RESELLER', 'INTERNAL']);
const OAuthClientStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']);

export const OAuthClientSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: OAuthClientTypeSchema,
  status: OAuthClientStatusSchema,
  allowedScopes: z.array(z.string()),
  rateLimit: z.number().int().nullable(),
  organizationName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  lastRotatedAt: z.string().datetime().nullable(),
  rotationDays: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
});
export type OAuthClient = z.infer<typeof OAuthClientSchema>;

export const OAuthClientSummarySchema = z.object({
  id: z.string(),
  clientId: z.string(),
  name: z.string(),
  type: OAuthClientTypeSchema,
  status: OAuthClientStatusSchema,
  organizationName: z.string().nullable(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
});
export type OAuthClientSummary = z.infer<typeof OAuthClientSummarySchema>;

// ============================================================================
// Social Auth (Google ID token)
// ============================================================================

export const GoogleIdTokenRequestSchema = z.object({
  idToken: z.string().min(1),
});
export type GoogleIdTokenRequest = z.infer<typeof GoogleIdTokenRequestSchema>;

export * from './staff';
export * from './pii';
